import rest from "@/rest-client";
import createCrudVuexModule from "@/store/crud";

export const organizationRisk = createCrudVuexModule({
  namespaced: true,
  path: "/risk",
  options: {
    localizedFields: ["name", "description", "weaknesses", "threats", "appliedControl", "note", "preventionPlanNote", "currentControls", "riskPointGoal", "budgetPlan"],
  },

  state: {
    riskRatingDefinition: undefined,
    isDownloading: false,
    editedRatingDefinition: undefined,
    form: undefined,
    risk: undefined,
    riskDecision: undefined,
    riskId: undefined,
    isCreating: false,
    isEditing: false,
    isSendingApproval: false,
    selectedTab: "organizationRisk",
    selectedSubTab: "prevention",
    dashboard: undefined,
  },
  getters: {
    isDownloading: (state) => state.isDownloading,
    dashboard: (state) => state.dashboard,
    selectedTab: (state) => state.selectedTab,
    selectedSubTab: (state) => state.selectedSubTab,
    form: (state) => state.form,
    risk: (state) => state.risk,
    riskDecision: (state) => state.riskDecision,
    riskRatingDefinition: (state) => state.riskRatingDefinition,
    editedRatingDefinition: (state) => state.editedRatingDefinition,
    riskId: (state) => state.riskId,
    isCreating: (state) => state.isCreating,
    isEditing: (state) => state.isEditing,
    isReading: (state) => !state.isEditing && !state.isCreating,
    isCalculating: (state) => state.isCalculating,
    isSendingApproval: (state) => state.isSendingApproval,
  },
  mutations: {
    isDownloading: (state, value) => (state.isDownloading = value),
    dashboard: (state, value) => (state.dashboard = value),
    selectedTab: (state, value) => (state.selectedTab = value),
    selectedSubTab: (state, value) => (state.selectedSubTab = value),
    form: (state, value) => (state.form = value),
    risk: (state, value) => (state.risk = value),
    riskDecision: (state, value) => (state.riskDecision = value),
    riskRatingDefinition: (state, value) => (state.riskRatingDefinition = value),
    editedRatingDefinition: (state, value) => (state.editedRatingDefinition = value),
    riskId: (state, value) => (state.riskId = value),
    isCalculating: (state, value) => (state.isCalculating = value),
    isSendingApproval: (state, value) => (state.isSendingApproval = value),
    isCreating: (state, value) => {
      state.isCreating = value;

      state.riskId = undefined;
      state.risk = undefined;
      state.isEditing = false;
      state.form = undefined;
    },
    isEditing: (state, value) => {
      state.isEditing = value;
      if (value) {
        state.isCreating = false;
      }
    },
    reset: (state, value) => {
      state.riskId = undefined;
      state.risk = undefined;
      state.editedEntity = undefined;
      state.copingEntity = undefined;
      state.isEditing = false;
      state.isCreating = false;
      state.form = {};
    },
  },

  actions: {
    async dashboard({ state, commit }) {
      commit("isLoading", true);
      try {
        commit("dashboard", undefined);
        const response = await rest.get(`/risk/dashboard`);
        commit("dashboard", response.data);

        return response;
      } finally {
        commit("isLoading", false);
      }
    },
    async fetchDetail({ getters, commit, dispatch }, id) {
      try {
        commit("isLoading", true);
        const response = await rest.get(`/risk/${id}`);
        commit("risk", response.data);

        return response.data;
      } finally {
        commit("isLoading", false);
      }
    },
    async fetchDetailByDefinition({ getters, commit, dispatch }, { definition, libraryItem }) {
      try {
        // commit("isLoading", true);
        const response = await rest.post(`/risk/by/definition`, { definition, libraryItem });

        return response.data;
      } finally {
        // commit("isLoading", false);
      }
    },

    async fetchRiskDecision({ commit }) {
      try {
        commit("isLoading", true);
        const response = await rest.get(`/risk-decision`);
        commit("riskDecision", response.data);

        return response.data;
      } finally {
        commit("isLoading", false);
      }
    },

    async saveRiskDecision({ commit }, payload) {
      try {
        commit("isLoading", true);
        const response = await rest.put(`/risk-decision`, { item: payload });
        return response;
      } finally {
        commit("isLoading", false);
      }
    },
    async fetchRiskRatingDefinition({ commit }) {
      try {
        commit("isLoading", true);
        const response = await rest.get(`/risk-rating-definition`);
        commit("riskRatingDefinition", response.data);

        return response.data;
      } finally {
        commit("isLoading", false);
      }
    },

    async saveRiskRatingDefinition({ commit }, payload) {
      try {
        commit("isLoading", true);
        const response = await rest.put(`/risk-rating-definition`, { item: payload });
        return response;
      } finally {
        commit("isLoading", false);
      }
    },

    async calculate({ getters, commit, dispatch }, payload) {
      try {
        commit("isCalculating", true);
        const response = await rest.post(`/risk/calculate`, { item: payload });
        return response;
      } finally {
        commit("isCalculating", false);
      }
    },

    async save({ dispatch }, payload) {
      if (payload._id) {
        return dispatch("update", payload);
      } else {
        return dispatch("create", payload);
      }
    },

    async create({ getters, commit, dispatch }, payload) {
      try {
        commit("isUpdating", true);
        payload = await dispatch("preparePayload", {
          editedEntity: getters.editedEntity,
          form: payload,
        });

        const response = await rest.post(`/risk`, { item: payload });
        commit("editedEntity", undefined);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    async update({ getters, commit, dispatch }, payload) {
      try {
        commit("isUpdating", true);
        payload = await dispatch("preparePayload", {
          editedEntity: getters.editedEntity,
          form: payload,
        });

        const response = await rest.put(`/risk`, { item: payload });

        commit("editedEntity", undefined);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    //Approval

    async sendApproval({ getters, commit, dispatch }, payload) {
      try {
        commit("isSendingApproval", true);

        const response = await rest.post(`/risk/send-approval`, payload);
        return response;
      } finally {
        commit("isSendingApproval", false);
      }
    },
    async resolveApproval({ getters, commit, dispatch }, payload) {
      try {
        commit("isSendingApproval", true);

        const response = await rest.post(`/risk/resolve-approval`, payload);
        return response;
      } finally {
        commit("isSendingApproval", false);
      }
    },
    //
    async download({ commit }, payload) {
      commit("isDownloading", true);
      try {
        const response = await rest.post("/risk/download", payload);
        return response;
      } finally {
        commit("isDownloading", false);
      }
    },
  },
});

export const organizationRiskAssetGroupRelation = createCrudVuexModule({
  namespaced: true,
  path: "/risk-asset-group-relation",
  options: { localizedFields: ["name", "description"] },

  state: {
    highestRatedRelation: undefined,
  },
  getters: {
    highestRatedRelation: (state) => state.highestRatedRelation,
  },
  mutations: {
    highestRatedRelation(state, payload) {
      state.highestRatedRelation = payload;
    },
  },
  actions: {
    async fetchHighestRatedRelation({ commit }, pagination) {
      try {
        commit("isLoading", true);
        const response = await rest.post(`/risk-asset-group-relation/paginate`, { pagination });
        return response.data?.docs[0];
      } finally {
      }
    },
  },
});
export const riskAppliedControlRecommendation = {
  namespaced: true,
  state: {
    recommendations: null,
    showRecommendations: false,
  },
  getters: {
    recommendations: (state) => state.recommendations,
    showRecommendations: (state) => state.showRecommendations,
  },
  mutations: {
    recommendations: (state, value) => {
      state.recommendations = value;
    },
    showRecommendations: (state, value) => {
      state.showRecommendations = value;
    },
  },
  actions: {
    async paginateRecommendation({ commit, rootGetters }, { pagination, riskDefinition }) {
      try {
        if (riskDefinition) {
          const response = await rest.post(`/recommendation/paginate/risk-applied-control`, {
            pagination,
            riskDefinition,
          });
          commit("recommendations", response.data);

          return response;
        }
      } finally {
      }
    },
  },
};

export const organizationRiskAppliedControl = createCrudVuexModule({
  namespaced: true,
  path: "/risk-applied-control",
  moduleName: `risk-applied-control`,
});

export const organizationRiskThreatDefinition = createCrudVuexModule({
  namespaced: true,
  path: "/risk-threat-definition",
  moduleName: `risk-threat-definition`,
});

export const organizationRiskWeaknessDefinition = createCrudVuexModule({
  namespaced: true,
  path: "/risk-weakness-definition",
  moduleName: `risk-weakness-definition`,
});
